import React, { useMemo } from "react";

import { cx, formatNumber, formatNumericCurrency, useTranslation } from "utils";

import { CURRENCY_CODE, CurrencyCode, NO_VALUE, SIDE_BY_SIDE_ROW_KEYS, SideBySideRowKey } from "consts";

import { Icon, SoilSightLoader, Tooltip, Typography } from "components";

import { SectionHeading } from "pages/app/scenario-analysis/components/SectionHeading";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { formatWholeSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import { SoilSightOutcomesByScenario, scenarioLineColor } from "pages/app/scenario-analysis/utils/forecast";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";
import { SideBySideMetrics, buildSideBySideTable } from "pages/app/scenario-analysis/utils/sideBySide";

export interface SideBySideSectionProps {
  /** In the order the cards and the chart use, which is the order of the columns. */
  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  currency: CurrencyCode | null;
  /** The same scores the Key insights resilience card reads, so the two surfaces cannot disagree. */
  resilienceScores: ScenarioResilienceByScenarioId;
  /** Figures no longer match the inputs, so the table waits rather than comparing stale numbers. */
  isStale: boolean;
  isCalculating: boolean;
  /** Opens the Side by side explainer. */
  onExplain: () => void;
}

/** Two columns is the least this table can say anything with: one scenario has nothing to sit beside. */
const MIN_COMPARABLE_SCENARIOS = 2;

/** From this many columns the figures take the compact size, so a value like "Never behind" stays on one line. */
const DENSE_SCENARIO_COUNT = 4;

/** Primary figure and the line under it, already formatted for one scenario's cell. */
type CellContent = { primary: string; secondary?: string };

/** The plain version of the cards and the chart: the same measures, one column per scenario. */
export const SideBySideSection = ({
  scenarios,
  soilSightOutcomes,
  projectionYears,
  currency,
  resilienceScores,
  isStale,
  isCalculating,
  onExplain,
}: SideBySideSectionProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const resolvedCurrency = currency ?? CURRENCY_CODE.EUR;

  const table = useMemo(
    () => buildSideBySideTable({ scenarios, outcomes: soilSightOutcomes, projectionYears, resilienceScores }),
    [scenarios, soilSightOutcomes, projectionYears, resilienceScores]
  );

  const perHectare = (value: string) => t("scenarioComparison.perHectare", { value });
  /* Values arrive already rounded, so formatting only adds the symbol and separators. */
  const money = (value: number, signDisplay: "auto" | "exceptZero" = "auto") =>
    perHectare(formatNumericCurrency(value, { currency: resolvedCurrency, currencyDisplay: "narrowSymbol", decimals: 0, signDisplay }));

  const ROW_LABELS: Record<SideBySideRowKey, string> = {
    totalMargin: t("sideBySide.rows.totalMargin.label", { years: projectionYears }),
    extraVsBaseline: t("sideBySide.rows.extraVsBaseline.label"),
    yearsBehind: t("sideBySide.rows.yearsBehind.label"),
    deepestDip: t("sideBySide.rows.deepestDip.label"),
    resilience: t("sideBySide.rows.resilience.label"),
  };

  const ROW_TOOLTIPS: Record<SideBySideRowKey, string> = {
    totalMargin: t("sideBySide.rows.totalMargin.tooltip", { years: projectionYears }),
    extraVsBaseline: t("sideBySide.rows.extraVsBaseline.tooltip"),
    yearsBehind: t("sideBySide.rows.yearsBehind.tooltip"),
    deepestDip: t("sideBySide.rows.deepestDip.tooltip"),
    resilience: t("sideBySide.rows.resilience.tooltip"),
  };

  const yearsBehindContent = (metrics: SideBySideMetrics): CellContent => {
    if (metrics.isStillBehind) return { primary: t("sideBySide.notWithinYears", { years: projectionYears }) };
    if (metrics.yearsBehind === null) return { primary: NO_VALUE };
    if (metrics.yearsBehind === 0) return { primary: t("sideBySide.aheadFromTheStart") };
    return { primary: t("sideBySide.yearsBehindFirst", { count: metrics.yearsBehind }) };
  };

  const resilienceContent = (metrics: SideBySideMetrics): CellContent => {
    if (metrics.resilienceScore === null) return { primary: NO_VALUE };
    return {
      primary: formatNumber({ number: metrics.resilienceScore, decimalPlaces: 1 }),
      secondary: metrics.resilienceBand === null ? undefined : t(`keyInsightCards.resilience.band.${metrics.resilienceBand}`),
    };
  };

  /* No meaningful value prints a dash: a zero there would read as a result. */
  const ROW_CONTENT: Record<SideBySideRowKey, (metrics: SideBySideMetrics) => CellContent> = {
    totalMargin: (metrics) =>
      metrics.cumulativeMargin === null
        ? { primary: NO_VALUE }
        : {
            primary: money(metrics.cumulativeMargin),
            secondary: metrics.averageAnnualMargin === null ? undefined : t("sideBySide.perYear", { value: money(metrics.averageAnnualMargin) }),
          },
    extraVsBaseline: (metrics) =>
      metrics.gainPercent === null
        ? { primary: NO_VALUE }
        : {
            primary: formatWholeSignedPercent(metrics.gainPercent),
            // A total over the horizon, so it says so and cannot be read against the yearly line above.
            secondary: metrics.gain === null ? undefined : t("sideBySide.overYears", { value: money(metrics.gain, "exceptZero"), years: projectionYears }),
          },
    yearsBehind: yearsBehindContent,
    // A dip levelled to zero means the rotation never fell behind, not a €0 shortfall.
    deepestDip: (metrics) =>
      metrics.deepestDip === null ? { primary: NO_VALUE } : { primary: metrics.deepestDip === 0 ? t("sideBySide.neverBehind") : money(metrics.deepestDip) },
    resilience: resilienceContent,
  };

  const hasAnyOutcomes = scenarios.some((scenario) => table.metricsByScenarioId[scenario.id]?.cumulativeMargin !== null);

  /* Literal keys, not a template: an interpolated prefix makes the copy scanner treat the whole block as used. */
  const renderMessage = (message: string, dataTestId: string) => (
    <div className="side-by-side-message" data-testid={dataTestId}>
      <Typography variant="paragraph-m" color="grey-600">
        {message}
      </Typography>
    </div>
  );

  const renderTable = () => (
    /* The column count drives the table's minimum width, so columns keep their size and the wrapper scrolls. */
    <div
      className="side-by-side-table-wrapper"
      style={{ "--side-by-side-columns": table.scenarios.length } as React.CSSProperties}
      data-dense={table.scenarios.length >= DENSE_SCENARIO_COUNT || undefined}
      tabIndex={0}
      role="region"
      aria-label={t("sideBySide.title")}
      data-testid="side-by-side-table-scroll"
    >
      <table className="side-by-side-table" data-testid="side-by-side-table">
        <thead>
          <tr>
            <th scope="col" className="side-by-side-measure-head">
              {t("sideBySide.measureHeader")}
            </th>
            {table.scenarios.map((scenario) => (
              <th key={scenario.id} scope="col" className="side-by-side-scenario-head">
                <span className="side-by-side-scenario-dot" style={{ background: scenarioLineColor(table.scenarios, scenario.id) }} aria-hidden />
                <span className="side-by-side-scenario-name">{scenario.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SIDE_BY_SIDE_ROW_KEYS.map((rowKey) => (
            <tr key={rowKey} className="side-by-side-row" data-testid={`side-by-side-row-${rowKey}`}>
              <th scope="row" className="side-by-side-measure">
                <span className="side-by-side-measure-inner">
                  {ROW_LABELS[rowKey]}
                  <Tooltip title={ROW_TOOLTIPS[rowKey]} placement="top">
                    <span className="side-by-side-measure-info" tabIndex={0} aria-label={ROW_TOOLTIPS[rowKey]} data-testid={`side-by-side-row-${rowKey}-info`}>
                      <Icon iconName="tabler/info-circle" size="s" aria-hidden />
                    </span>
                  </Tooltip>
                </span>
              </th>
              {table.scenarios.map((scenario) => {
                const metrics = table.metricsByScenarioId[scenario.id];
                const content = metrics ? ROW_CONTENT[rowKey](metrics) : { primary: NO_VALUE };
                const isBest = table.bestScenarioIdsByRow[rowKey].includes(scenario.id);
                // A dash centres against the row, like the measure name.
                const isEmpty = content.primary === NO_VALUE;
                return (
                  <td key={scenario.id} className={cx("side-by-side-cell", isEmpty && "side-by-side-cell-empty")}>
                    <span
                      className={cx("side-by-side-value", isBest && "side-by-side-value-best")}
                      data-testid={`side-by-side-cell-${rowKey}-${scenario.id}`}
                      data-best={isBest || undefined}
                    >
                      {content.primary}
                      {isBest ? <span className="side-by-side-best-label">{t("sideBySide.bestInRow")}</span> : null}
                    </span>
                    {content.secondary ? (
                      <span className="side-by-side-secondary" data-testid={`side-by-side-secondary-${rowKey}-${scenario.id}`}>
                        {content.secondary}
                      </span>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderBody = () => {
    if (isCalculating) return <SoilSightLoader size="m" dataTestId="side-by-side-calculating" />;
    if (scenarios.length < MIN_COMPARABLE_SCENARIOS) return renderMessage(t("sideBySide.notEnoughScenarios"), "side-by-side-not-enough-scenarios");
    if (isStale || !hasAnyOutcomes) return renderMessage(t("sideBySide.pending"), "side-by-side-pending");
    return renderTable();
  };

  return (
    <section className="side-by-side-section" data-testid="side-by-side-section">
      <SectionHeading
        title={t("sideBySide.title")}
        supporting={t("sideBySide.caption")}
        infoLabel={t("sideBySide.infoLabel")}
        onInfo={onExplain}
        dataTestId="side-by-side-section-heading"
      />

      {renderBody()}
    </section>
  );
};

export default SideBySideSection;
